import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "gap-4 md:grid md:grid-cols-8" }
const _hoisted_4 = {
  for: "users",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_5 = { class: "grid grid-cols-2 gap-4 md:grid-cols-8" }
const _hoisted_6 = { class: "space-y-2" }
const _hoisted_7 = { class: "text-sm font-medium text-gray-800" }
const _hoisted_8 = { class: "grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8" }

import { goBack } from '@/composables/useNavigation';
import { SelectOptionsService, toast, toastServiceError } from '@/services';
import WorkScheduleService from '@/services/api/WorkScheduleService';
import { useUiStore } from '@/stores';
import {
    FormAction,
    Permission,
    SelectOptionsServiceGetUsersResult,
    WorkScheduleFormState,
    WorkScheduleModel,
} from '@/types';
import useVuelidate, { Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { AxiosResponse } from 'axios';
import { computed, onMounted, reactive, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkScheduleFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const uiStore = useUiStore();
const route = useRoute();
const { t } = useI18n();

const props = __props;

const formState = reactive<WorkScheduleFormState>({
    name: null,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    timeBegin: '08:00:00',
    timeEnd: '17:00:00',
    isActive: true,
    users: [],
});

const rules = computed<ValidationArgs<WorkScheduleFormState>>(() => ({
    name: { required, maxLength: maxLength(50) },
    monday: { required },
    tuesday: { required },
    wednesday: { required },
    thursday: { required },
    friday: { required },
    saturday: { required },
    sunday: { required },
    timeBegin: { required },
    timeEnd: { required },
    isActive: { required },
    users: {},
}));

const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const v$: Ref<Validation<ValidationArgs<WorkScheduleFormState>>> = useVuelidate(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchWorkScheduleById = async (id: number) => {
    console.log(`buscando item id ${id}`);
    try {
        setLoading(true);
        const { data } = await WorkScheduleService.get<WorkScheduleModel>(id);
        Object.assign(formState, data);
        formState.users = (data.users || []).map((user) => user.id);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                has_extension: true,
                is_active: true,
            },
        });
        usersOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const isTimeBefore = (start: string, end: string): boolean => {
    const [h1, m1] = start.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    return h1 < h2 || (h1 == h2 && m1 < m2);
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    const noDaySelected = !(
        formState.monday ||
        formState.tuesday ||
        formState.wednesday ||
        formState.thursday ||
        formState.friday ||
        formState.saturday ||
        formState.sunday
    );

    if (noDaySelected) {
        toastServiceError(t('workSchedule.error.dayNotSelected'));
        return;
    }

    if (!isTimeBefore(formState.timeBegin, formState.timeEnd)) {
        toastServiceError(t('workSchedule.error.invalidTimeRange'));
        return;
    }

    try {
        setLoading(true);
        let response: AxiosResponse;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await WorkScheduleService.update(id, formState);
            toast.success(response.data.message);
            goBack({ name: 'ViewWorkScheduleById', params: { id } });
        } else {
            response = await WorkScheduleService.create(formState);
            toast.success(response.data.message);
            goBack({ name: 'ViewWorkScheduleById', params: { id: response.data.result.id } });
        }

        v$.value.$reset();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        const workScheduleId = Number(route.params.id);
        if (!isNaN(workScheduleId)) fetchWorkScheduleById(workScheduleId);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiTimePicker = _resolveComponent("UiTimePicker")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('workSchedule.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListWorkSchedule' } }),
          (
                            (props.action == _unref(FormAction).CREATE &&
                                _ctx.$can(_unref(Permission).CREATE_WORK_SCHEDULE)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_WORK_SCHEDULE))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { class: "space-y-4" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: _unref(v$).name.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
            name: "name",
            type: "text",
            label: _ctx.$t('workSchedule.labels.name'),
            class: "md:col-span-6 lg:col-span-4",
            errors: _unref(v$).name.$errors,
            onBlur: _unref(v$).name.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"])
        ]),
        _createElementVNode("div", null, [
          _createElementVNode("label", _hoisted_4, _toDisplayString(_ctx.$t('workSchedule.labels.users')), 1),
          _createVNode(_component_UiMultiselect, {
            id: "users",
            name: "users",
            label: "name",
            "value-prop": "id",
            modelValue: formState.users,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.users) = $event)),
            multiple: "",
            primitive: "",
            options: usersOptions.value
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_UiTimePicker, {
            modelValue: _unref(v$).timeBegin.$model,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).timeBegin.$model) = $event)),
            name: "timeBegin",
            label: _ctx.$t('workSchedule.labels.timeBegin'),
            class: "md:col-span-3 lg:col-span-2",
            errors: _unref(v$).timeBegin.$errors,
            onBlur: _unref(v$).timeBegin.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
          _createVNode(_component_UiTimePicker, {
            modelValue: _unref(v$).timeEnd.$model,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).timeEnd.$model) = $event)),
            name: "timeEnd",
            label: _ctx.$t('workSchedule.labels.timeEnd'),
            class: "md:col-span-3 lg:col-span-2",
            errors: _unref(v$).timeEnd.$errors,
            onBlur: _unref(v$).timeEnd.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("h3", _hoisted_7, _toDisplayString(_ctx.$t('workSchedule.labels.workDays')), 1),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).monday.$model,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).monday.$model) = $event)),
              name: "monday",
              label: _ctx.$t('workSchedule.labels.monday')
            }, null, 8, ["modelValue", "label"]),
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).tuesday.$model,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(v$).tuesday.$model) = $event)),
              name: "tuesday",
              label: _ctx.$t('workSchedule.labels.tuesday')
            }, null, 8, ["modelValue", "label"]),
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).wednesday.$model,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(v$).wednesday.$model) = $event)),
              name: "wednesday",
              label: _ctx.$t('workSchedule.labels.wednesday')
            }, null, 8, ["modelValue", "label"]),
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).thursday.$model,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(v$).thursday.$model) = $event)),
              name: "thursday",
              label: _ctx.$t('workSchedule.labels.thursday')
            }, null, 8, ["modelValue", "label"]),
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).friday.$model,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(v$).friday.$model) = $event)),
              name: "friday",
              label: _ctx.$t('workSchedule.labels.friday')
            }, null, 8, ["modelValue", "label"]),
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).saturday.$model,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((_unref(v$).saturday.$model) = $event)),
              name: "saturday",
              label: _ctx.$t('workSchedule.labels.saturday')
            }, null, 8, ["modelValue", "label"]),
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).sunday.$model,
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_unref(v$).sunday.$model) = $event)),
              name: "sunday",
              label: _ctx.$t('workSchedule.labels.sunday')
            }, null, 8, ["modelValue", "label"])
          ])
        ]),
        _createVNode(_component_UiCheckboxInput, {
          modelValue: _unref(v$).isActive.$model,
          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
          name: "isActive",
          label: _ctx.$t('workSchedule.labels.isActive')
        }, null, 8, ["modelValue", "label"])
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})